import { useMemo } from "react";

import { useQuery } from "@tanstack/react-query";
import { compact, flatMap, groupBy, maxBy } from "lodash/fp";
import { useParams } from "react-router";
import { useSearchParams } from "react-router-dom";

import type { TopicEntity, TopicEntityListParams } from "@app/analytics/types";
import apiClient from "@app/common/api/apiClient";
import useAuth from "@app/common/auth/useAuth";
import { hasRetryableStatus } from "@app/common/utils";

const getLatestDailyUpdate = (
  entities?: TopicEntity[]
): TopicEntity | undefined => {
  if (!entities) return;
  if (entities.length <= 1) return entities[0];

  return maxBy((e) => e.endTime.toMillis(), entities);
};

const EMPTY_ENTITIES: TopicEntity[] = [];

const useTopicReportEntities = () => {
  const { user } = useAuth();
  const { topicId } = useParams();
  const [queryParams] = useSearchParams();

  const impersonateUserId = queryParams.get("iid") ?? undefined;

  const topicEntitiesQuery = useQuery({
    queryKey: ["topic", topicId, user?.id, impersonateUserId, "entities"],
    queryFn: () => {
      if (!topicId) return EMPTY_ENTITIES;
      const params: TopicEntityListParams = {
        entityKinds: ["action_item", "daily_summary", "key_question"],
        impersonateUserId,
      };
      return apiClient.topics.listTopicEntities(topicId, params);
    },
    enabled: Boolean(topicId) && Boolean(user?.id),
    retry: (failureCount, error) =>
      failureCount < 3 && hasRetryableStatus(error),
  });

  const allEntities = topicEntitiesQuery.data ?? EMPTY_ENTITIES;

  const { citations, latestUpdate, entitiesByType } = useMemo(() => {
    const citations = compact(flatMap("citations", allEntities));
    const entitiesByType = groupBy("kind", allEntities);
    const latestUpdate = getLatestDailyUpdate(entitiesByType.daily_summary);
    return { citations, latestUpdate, entitiesByType };
  }, [allEntities]);

  return {
    allEntities,
    actionItems: entitiesByType.action_item ?? EMPTY_ENTITIES,
    keyQuestions: entitiesByType.key_question ?? EMPTY_ENTITIES,
    dailyUpdates: entitiesByType.daily_summary ?? EMPTY_ENTITIES,
    latestUpdate,
    citations,
    isLoading: topicEntitiesQuery.isLoading,
    isError: topicEntitiesQuery.isError,
  };
};

export default useTopicReportEntities;
